/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import React, { type ReactElement, useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next'
import LogTool from '../../../logger/logTools'
import { ContractTabType, useCountContext, useUserContext } from '../../../utils/context'
import GetConstant from '../utils/constants'
import { type FormFieldType } from '../../Form'
import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'
import { Form } from '../../Form'
import { isPrivileged } from '../../../utils/functions'
import { type FileInfo, type FileUploadStatus } from '../../../utils/types'
import FileInput from '../../../components/inputs/FileInput'
import InputFileList from '../../../components/widgets/InputFileList'
import FilesDrawer from '../../Filesystem/components/FilesDrawer'
import { LoadingButton } from '@mui/lab'
import { type Bill, type BillInput } from '../utils/types'
import { usePatchContract } from '../hooks/useContractMutations'
import { toast } from 'react-toastify';
import { deleteFileFromUrl, uploadFileToUrl, fetchFileList } from '../../Filesystem/api/fileApi'
import useFetchExistingFiles from '../../../hooks/useFetchExistingFiles'
import { deriveFilesUrl } from '../../Filesystem/utils/filesHelper'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: BillInput | undefined
    setState: React.Dispatch<React.SetStateAction<BillInput | undefined>>
  }
  onClose?: () => void
  onCancel?: () => void
  onCreateConfirm?: (input: BillInput) => Promise<string | undefined>
  enableEditMode?: boolean
  onEditConfirm?: (input: Bill | BillInput) => Promise<string | undefined>
  onFilesUploaded?: () => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function BillStaffDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    formState,
    onClose = () => null,
    onCreateConfirm = () => null,
    enableEditMode = false,
    onEditConfirm = () => null,
    onFilesUploaded = () => null,
  } = props

  const { t } = useTranslation(['contract', 'common'])
  const log = new LogTool({ context: 'BillStaffDrawer', enable: true, logLevel: 'warn' })
  const { user } = useUserContext()
  const { setActiveTabType } = useCountContext()
  const patchContractMutation = usePatchContract()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [waiting, setWaiting] = useState(false)
  const [prevOpen, setPrevOpen] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start ---------------------------- */
  const [fileInput, setFileInput] = useState<FileInfo[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([])
  const [openFilesDrawer, setOpenFilesDrawer] = useState(false)
  /* ----------------------------- Data states end ----------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (open && !prevOpen) {
      setPrevOpen(true)
    }
  }, [open])

  useEffect(() => {
    if (fileInput.length > 0 && fileInput.every(file => file.uploadStatus === 'success')) {
      setWaiting(false)
      handleClose()
    }
  }, [fileInput])

  // Fetch existing files when drawer opens, formState changes, or FilesDrawer opens
  useFetchExistingFiles({
    open,
    formState,
    openFilesDrawer,
    setUploadedFiles,
    logContext: 'bill',
  })

  // Reset fileInput and waiting when drawer closes
  useEffect(() => {
    if (!open || !formState.state) {
      setFileInput([])
      setWaiting(false)
    }
  }, [open, formState.state])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Setter for the uploadStatus property on a FileInfo object within this component.
   * Do not use this function if you want to update multiple files at once as the component is rerendered after every status update!
   * @param file the file on which the uploadStatus should be updated.
   * @param status the updated uploadStatus.
   */
  function setFileUploadStatus(file: FileInfo, status: FileUploadStatus): void {
    setFileInput((prev: FileInfo[]): FileInfo[] =>
      prev.map((listedFile: FileInfo): FileInfo => {
        if (listedFile.name === file.name) {
          // add an upload-status of success to the file info object or overwrite it with the provided status
          return Object.assign(file, { uploadStatus: status })
        }
        return listedFile
      })
    )
  }

  /**
   * Wrapper for the uploadFile function that specifies the onSuccess and onError callbacks.
   * This function simply allows us to upload files using the same behavior at multiple places
   * within this component without rewriting the callback functions all the time.
   * @param file the file to upload.
   * @param url the url to upload the file to.
   */
  async function uploadBillFile(file: FileInfo, url: string): Promise<void> {
    try {
      await uploadFileToUrl(url, file as File)
      setFileUploadStatus(file, 'success')
    } catch (error) {
      log.error('Error uploading bill file', error)
      setFileUploadStatus(file, 'uploadError')
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleCreateBill = async (): Promise<void> => {
    if (!formState.state) {
      throw new Error('Form state is undefined')
    }
    log.debug('handleCreateBill -> ', formState.state)
    setWaiting(true)

    if (fileInput.length > 0) {
      setFileInput((prev: FileInfo[]) =>
        prev.map(file => Object.assign(file, { uploadStatus: 'loading' }))
      )
    }
    const billFilesUrl = await onCreateConfirm(formState.state)
    if (fileInput.length > 0 && billFilesUrl) {
      for (const file of fileInput) {
        await uploadBillFile(file, billFilesUrl)
      }
      
      // Refresh uploaded files list after successful upload
      if (formState.state?.self) {
        try {
          const filesUrl = deriveFilesUrl(formState.state)
          if (filesUrl) {
            const fileResponse = await fetchFileList(filesUrl)
            setUploadedFiles(fileResponse.results)
          }
        } catch (error) {
          log.error('Error refreshing uploaded files after upload', error)
        }
      }
      
      // Notify parent that files were uploaded so it can refresh FilesDrawer
      onFilesUploaded()
    }
    setWaiting(false)
    handleClose()

    // Update contract status
    try {
      const newStatus = formState.state.paid === true ? 6 : 5
      await patchContractMutation.mutateAsync({
        idOrSelf: formState.state.contract.self,
        data: { contractStatus: newStatus },
      })
      log.debug('Contract status updated successfully')
    } catch (error) {
      log.error('Error updating contract status', error)
      toast.error(
        t('contract:feedback.error.updateContractStatus', {
          defaultValue: 'Could not update contract status.',
        })
      )
    }

    // Switch to Bills tab after creating a bill
    setActiveTabType(ContractTabType.BILLS)
  }

  const handleEditBill = async (): Promise<void> => {
    log.debug('handleEditBill -> ', formState.state)
    setWaiting(true)
    if (!formState.state) {
      throw new Error('Form state is undefined')
    }

    // Update contract status if bill is marked as paid
    if (formState.state.paid === true) {
      try {
        await patchContractMutation.mutateAsync({
          idOrSelf: formState.state.contract.self,
          data: { contractStatus: 6 },
        })
        log.debug('Contract status updated successfully')
      } catch (error) {
        log.error('Error updating contract status', error)
        toast.error(
          t('contract:feedback.error.updateContractStatus', {
            defaultValue: 'Could not update contract status.',
          })
        )
      }
    }

    if (fileInput.length > 0) {
      setFileInput((prev: FileInfo[]) =>
        prev.map(file => Object.assign(file, { uploadStatus: 'loading' }))
      )
    }
    const billFilesUrl = await onEditConfirm(formState.state)
    if (fileInput.length > 0 && billFilesUrl) {
      for (const file of fileInput) {
        await uploadBillFile(file, billFilesUrl)
      }
      
      // Refresh uploaded files list after successful upload
      if (formState.state?.self) {
        try {
          const filesUrl = deriveFilesUrl(formState.state)
          if (filesUrl) {
            const fileResponse = await fetchFileList(filesUrl)
            setUploadedFiles(fileResponse.results)
          }
        } catch (error) {
          log.error('Error refreshing uploaded files after upload', error)
        }
      }
      
      // Notify parent that files were uploaded so it can refresh FilesDrawer
      onFilesUploaded()
    } else {
      setWaiting(false)
      handleClose()
    }
  }

  const handleBill = async (operation: 'update' | 'create'): Promise<void> => {
    log.debug('handleBill -> ', operation)
    operation === 'create' ? await handleCreateBill() : await handleEditBill()
  }

  /**
   * Remove a file from fileInput list before it is uploaded
   * @param file
   */
  const handleRemoveFileFromList = (file: FileInfo): void => {
    // update rendered files list
    setFileInput(prevFileList => {
      // get the location of the file to remove within the files list
      const fileIndex = prevFileList.findIndex(listedFile => listedFile.name === file.name)
      // remove the file from the list. Note that this does not work on the prevFileList directly
      const updatedFileList = [...prevFileList]
      updatedFileList.splice(fileIndex, 1)

      // return the files list where the file is removed
      return updatedFileList
    })
  }
  /**
   * (Re)upload a file to the specified url.
   * @param file The file to upload
   * @param url The url to upload the file to.
   */
  const handleReUploadFile = async (file: FileInfo, url: string): Promise<void> => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // retry to upload the file
    await uploadBillFile(file, url)
  }

  /**
   * Delete a file that was uploaded already.
   * @param file The file to delete.
   */
  const handleDeleteFile = async (file: FileInfo): Promise<void> => {
    try {
      await deleteFileFromUrl(file.self as string)
      // Remove from uploadedFiles
      setUploadedFiles((prev) => prev.filter((f) => f.self !== file.self))
    } catch (error) {
      log.error('Error deleting bill file', error)
      throw error // Re-throw so UploadedFilesTable can handle it
    }
  }

  const handleDeleteMultipleFiles = async (files: FileInfo[]): Promise<void> => {
    try {
      await Promise.all(
        files.map(async (file) => {
          if (!file.self) return
          await deleteFileFromUrl(file.self)
        })
      )
      // Remove all deleted files from uploadedFiles
      const deletedSelfs = new Set(files.map((f) => f.self).filter(Boolean))
      setUploadedFiles((prev) => prev.filter((f) => !f.self || !deletedSelfs.has(f.self)))
    } catch (error) {
      log.error('Error deleting multiple bill files', error)
      throw error
    }
  }

  const handleClose = (preventOnClose = false): void => {
    // reset form state
    setWaiting(false)
    formState.setState(undefined)
    setFileInput([])
    setUploadedFiles([])
    setOpen(false)
    setPrevOpen(false)
    !preventOnClose && onClose()
  }
  /* ------------------------- Callback functions end ------------------------- */
  /* ------------------------- Render constants start ------------------------- */
  const billStaffFormFields = GetConstant({ name: 'billStaffFormFields' }) as FormFieldType[]
  
  // Compute files URL for FilesDrawer - must be before early return to satisfy hooks rules
  const filesDrawerUrl = useMemo(() => {
    // Always construct the files URL directly from formState.state.self if available
    if (formState.state?.self) {
      const url = `${formState.state.self}files/`
      return url
    }
    // Fallback: derive URL from uploadedFiles if available
    if (uploadedFiles.length > 0 && uploadedFiles[0]?.self) {
      const fileSelf = uploadedFiles[0].self
      // Extract parent URL from file's self (e.g., "/v1/contract/bill_files/1/" -> "/v1/contract/bill_files/")
      const trimmed = fileSelf.endsWith('/') ? fileSelf.slice(0, -1) : fileSelf
      const lastSlash = trimmed.lastIndexOf('/')
      if (lastSlash !== -1) {
        const url = `${trimmed.slice(0, lastSlash + 1)}`
        return url
      }
    }
    return undefined
  }, [formState.state?.self, uploadedFiles])
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */

  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('FormInput -> ', formState.state)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (!formState.state) {
    return <></>
  }

  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        onClose={onClose}
        size="big"
        title={
          enableEditMode
            ? t('contract:content.label.editBill')
            : t('contract:content.label.createBill')
        }
        customActions={
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <Button
              id="cancelButton"
              onClick={() => setOpen(false)}
              color="inherit"
              variant="contained"
              sx={{ mr: 1, textTransform: 'none' }}
            >
              {t('common:interaction.button.cancel')}
            </Button>
            <LoadingButton
              id="createButton"
              variant="contained"
              sx={{ marginLeft: '10px', color: 'whitesmoke', textTransform: 'none' }}
              loading={waiting}
              onClick={enableEditMode ? () => handleBill('update') : () => handleBill('create')}
            >
              {enableEditMode
                ? t('common:interaction.button.save')
                : t('common:interaction.button.create')}
            </LoadingButton>
          </Box>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="contractPrice"
              label={t('contract:content.label.price')}
              variant="outlined"
              fullWidth
              disabled
              value={
                formState.state?.contract?.price
                  ? Object.keys(formState.state.contract.price).map(
                      p =>
                        `${(formState.state?.contract.price as any)[p]} ${t('request:content.label.pieces')}: ${p} €`
                    )
                  : t('common:content.label.notAvailable')
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Form
              editing={true}
              formFields={billStaffFormFields}
              formObject={formState.state}
              onChange={(value: any) => {
                formState.setState(value)
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '15px', marginBottom: '10px' }}>
              <Typography variant="h6">
                {t('common:content.label.documents')}
              </Typography>
              
              {/* Already Uploaded Files - Button to open FilesDrawer */}
              {uploadedFiles.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenFilesDrawer(true)}
                  sx={{ 
                    textTransform: 'none',
                    color: 'white',
                    display: 'flex',
                  }}
                >
                  {t('common:content.label.viewUploadedFiles', { count: uploadedFiles.length }) || 
                   `${t('common:content.label.uploadedFiles')} (${uploadedFiles.length})`}
                </Button>
              )}
            </Box>
            <FileInput
              inputState={{ state: fileInput, setState: setFileInput }}
              maxAllowedFiles={10}
            />
            {fileInput.length > 0 && (
              <InputFileList
                files={fileInput}
                noFilesInfo=""
                enableDownloadFile={false}
                onRemoveFileFromList={handleRemoveFileFromList}
                onReUploadFile={handleReUploadFile}
                onDeleteFile={handleDeleteFile}
              />
            )}
          </Grid>
        </Grid>
      </MultilevelDrawer>
      <FilesDrawer
        key="bill-staff-drawer-files-drawer"
        open={openFilesDrawer}
        setOpen={setOpenFilesDrawer}
        files={uploadedFiles.length > 0 ? uploadedFiles : filesDrawerUrl}
        title={t('common:content.label.uploadedFiles')}
        size="big"
        refreshKey={uploadedFiles.length}
      />
    </>
  )
}
